import type { PostgrestError } from "@supabase/supabase-js"
import { advanceGeneratedColumns, advanceRequestGeneratedColumns, expenseGeneratedColumns, type Tables } from "../../types/supabase"
import { NotNull } from "../../types/util"

/**
 * The list of tables which can be synced.
 * A syncable table must define the following columns:
 * - id: string
 * - membership_id: string
 * - updated_at: string
 * - deleted_at: string | null
 */
export type SyncableTable = typeof SyncableTables[number]

export const SyncableTables = [
  'expenses',
  'incidents',
  'attachments',
  'submissions',
  'advances',
  'advance_requests',
  'notifications',
  'providers',
  'todos',
  'blobs',
  'conversations',
  'messages',
  'phone_calls',
] as const

export function raiseUnknownSyncableTable(table: never): never {
  throw new Error(`Unknown syncable table: ${table}`)
}

export function isTableWithGeneratedColumns(table: SyncableTable): table is SyncableTable & keyof typeof GeneratedColumns {
  return table in GeneratedColumns
}

export const GeneratedColumns = {
  'expenses': expenseGeneratedColumns,
  'advances': advanceGeneratedColumns,
  'advance_requests': advanceRequestGeneratedColumns,
} as const


// Assert that all syncable tables have an ID, updated_at, and deleted_at.
// If this causes a Typescript error, it means a table listed above
// does not have the correct column types (as defined in src/types/supabase.ts)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _assertSyncableTable: {
  id: string,
  updated_at: string,
  deleted_at?: string | null,
} = {} as Tables[SyncableTable]['Row']

export type Add<Table extends SyncableTable> = {
  type: 'add',
  table: Table,
  record: NotNull<Tables[Table]['Insert'], 'id' | 'updated_at'>
}

export type Update<Table extends SyncableTable> = {
  type: 'update',
  table: Table,
  /** Partial: only the columns that changed, plus the id and the concurrency token. */
  record: NotNull<Tables[Table]['Update'], 'id' | 'updated_at'>
}

export type Delete<Table extends SyncableTable> = {
  type: 'delete',
  table: Table,
  record: { id: string, updated_at: string, deleted_at?: string | null }
}

export type SyncAction<Table extends SyncableTable = SyncableTable> =
  (Add<Table> | Delete<Table> | Update<Table>) & {
    /**
     * How many times the server has rejected this action. Tracked on the action so
     * it lives and dies with the queue entry, and persists across reloads with it.
     * See lib/sync/unrecoverable.ts.
     */
    attempts?: number

    /**
     * The Redux action type that produced this write, e.g. `submissions/addSubmission`.
     *
     * Sent to the backend so todo reconciliation can tell "this todo's subject went away"
     * from "the member did the thing this todo was recording" -- the distinction its
     * `completed_with_actions` column encodes. The server allow-lists it, so an unknown
     * value is ignored rather than trusted.
     *
     * Optional because queues persisted before this shipped have no such field, and
     * because reporting nothing is always safe: the backend then falls back to the
     * behavior it had when it only ever heard 'delete' or nothing at all.
     */
    action_type?: string
  }

export type SyncResult<
  TAction extends SyncAction<SyncableTable> = SyncAction<SyncableTable>
> = {
  /**
   * The highest `updated_at` across applied rows. Diagnostic only — it is the one thing
   * redactSyncSlice keeps from a sync result, so a Sentry report says how far the client
   * had got.
   */
  updated_at: string

  /** These actions were applied */
  applied: TAction[],
  /** These actions were rejected: the server refused them and retrying cannot help */
  rejected: TAction[],
  /** These actions caused an error and will be retried */
  error: Array<TAction & { error: Error | PostgrestError }>
}
